import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import './LandingPage.css';

// Single subtle gradient orb
const GradientOrb: React.FC = () => (
  <div className="gradient-orbs">
    <div className="orb orb-1"></div>
  </div>
);

// Grid background
const GridBackground: React.FC = () => (
  <div className="grid-background">
    <div className="grid-lines"></div>
  </div>
);

// Scroll reveal hook
function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            // Stagger children
            const children = entry.target.querySelectorAll('.reveal-child');
            children.forEach((child, i) => {
              (child as HTMLElement).style.transitionDelay = `${i * 100}ms`;
              child.classList.add('revealed');
            });
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

// Feature card component
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
  gradient: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, delay, gradient }) => (
  <div
    className="feature-card"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="feature-icon">
      {icon}
    </div>
    <h3 className="feature-title">{title}</h3>
    <p className="feature-description">{description}</p>
    <div className="feature-glow" style={{ background: gradient }}></div>
  </div>
);

// Animated counter component
interface AnimatedCounterProps {
  value: string;
  label: string;
  delay: number;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ value, label, delay }) => {
  const [displayValue, setDisplayValue] = useState('0');
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const parseValue = (val: string) => {
    const match = val.match(/^([<>]?)(\d+\.?\d*)(.*)$/);
    if (match) {
      return {
        prefix: match[1] || '',
        number: parseFloat(match[2]),
        suffix: match[3] || '',
        hasDecimal: match[2].includes('.')
      };
    }
    return { prefix: '', number: 0, suffix: val, hasDecimal: false };
  };

  const { prefix, number, suffix, hasDecimal } = parseValue(value);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);

            const duration = 2000;
            const startTime = performance.now();
            const startDelay = delay;

            setTimeout(() => {
              const animate = (currentTime: number) => {
                const elapsed = currentTime - startTime - startDelay;
                const progress = Math.min(elapsed / duration, 1);
                const easeOut = 1 - Math.pow(1 - progress, 3);
                const currentValue = number * easeOut;

                if (hasDecimal) {
                  setDisplayValue(currentValue.toFixed(1));
                } else {
                  setDisplayValue(Math.floor(currentValue).toString());
                }

                if (progress < 1) {
                  requestAnimationFrame(animate);
                } else {
                  setDisplayValue(hasDecimal ? number.toFixed(1) : number.toString());
                }
              };

              requestAnimationFrame(animate);
            }, startDelay);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [number, hasDecimal, delay, hasAnimated]);

  return (
    <div className="stat" ref={ref} style={{ animationDelay: `${delay}ms` }}>
      <span className="stat-value">{prefix}{displayValue}{suffix}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
};

// Email validation
function isValidEmail(email: string): boolean {
  const re = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
  return re.test(email);
}

// Main Landing Page
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, login, register } = useAuth();
  const { resolvedTheme, toggleTheme } = useTheme();
  const [isLoaded, setIsLoaded] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Waitlist form state
  const [waitlistName, setWaitlistName] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistStatus, setWaitlistStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [waitlistError, setWaitlistError] = useState('');

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setWaitlistError('');

    const trimmedName = waitlistName.trim();
    const trimmedEmail = waitlistEmail.trim();

    if (!trimmedName) {
      setWaitlistError(t('landing.errorName'));
      return;
    }
    if (!trimmedEmail) {
      setWaitlistError(t('landing.errorEmail'));
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setWaitlistError(t('landing.errorEmailInvalid'));
      return;
    }

    setWaitlistStatus('submitting');
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmedEmail, name: trimmedName }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || `Request failed (${res.status})`);
      }
      setWaitlistStatus('success');
    } catch (err: any) {
      setWaitlistStatus('error');
      setWaitlistError(err.message || 'Something went wrong. Please try again.');
    }
  };

  // Scroll reveal refs
  const problemRef = useScrollReveal();
  const platformRef = useScrollReveal();
  const usecase1Ref = useScrollReveal();
  const usecase2Ref = useScrollReveal();
  const b2cRef = useScrollReveal();
  const ctaRef = useScrollReveal();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Nav scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setNavScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auth handlers — redirect to Keycloak
  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      login();
    }
  };

  const handleSignUp = () => {
    register();
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className={`landing-page ${isLoaded ? 'loaded' : ''}`}>
      {/* Background effects */}
      <GridBackground />
      <GradientOrb />

      {/* Navigation */}
      <nav className={`landing-nav ${navScrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-logo">
          <span className="logo-text">didi</span>
        </div>
        <div className="nav-links">
          <a href="#platform" className="nav-link">{t('nav.platform')}</a>
          <a href="#use-cases" className="nav-link">{t('nav.useCases')}</a>
          <a href="#how-it-works" className="nav-link">{t('nav.howItWorks')}</a>
        </div>
        <div className="nav-actions">
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            aria-label={t('nav.toggleTheme')}
          >
            {resolvedTheme === 'dark' ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="12" cy="12" r="5"/>
                <line x1="12" y1="1" x2="12" y2="3"/>
                <line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/>
                <line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>
          {isAuthenticated ? (
            <button className="btn btn-primary" onClick={handleDashboard}>
              {t('nav.dashboard')}
            </button>
          ) : (
            <button className="btn btn-primary" onClick={handleGetStarted}>
              {t('nav.getStarted')}
            </button>
          )}
          <button
            className="nav-hamburger"
            onClick={() => setIsMobileNavOpen(true)}
            aria-label={t('nav.openMenu')}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Mobile Nav Overlay + Drawer */}
      {isMobileNavOpen && (
        <div className="mobile-nav-overlay" onClick={() => setIsMobileNavOpen(false)} />
      )}
      <div className={`mobile-nav-drawer ${isMobileNavOpen ? 'open' : ''}`}>
        <button
          className="mobile-nav-close"
          onClick={() => setIsMobileNavOpen(false)}
          aria-label={t('nav.closeMenu')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="mobile-nav-links">
          <a href="#platform" className="mobile-nav-link" onClick={() => setIsMobileNavOpen(false)}>{t('nav.platform')}</a>
          <a href="#use-cases" className="mobile-nav-link" onClick={() => setIsMobileNavOpen(false)}>{t('nav.useCases')}</a>
          <a href="#how-it-works" className="mobile-nav-link" onClick={() => setIsMobileNavOpen(false)}>{t('nav.howItWorks')}</a>
        </div>
        <div className="mobile-nav-actions">
          {isAuthenticated ? (
            <button className="btn btn-glow" onClick={() => { setIsMobileNavOpen(false); handleDashboard(); }}>
              {t('nav.dashboard')}
            </button>
          ) : (
            <button className="btn btn-glow" onClick={() => { setIsMobileNavOpen(false); handleGetStarted(); }}>
              {t('nav.getStarted')}
            </button>
          )}
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-badge">
          <span className="badge-dot"></span>
          <span>{t('landing.badge')}</span>
        </div>

        <h1 className="hero-title">
          {t('landing.heroTitle')}
        </h1>

        <p className="hero-subtitle">
          {t('landing.heroSubtitle')}
        </p>

        <div className="hero-actions">
          <button className="btn btn-glow" onClick={handleGetStarted}>
            <span>{t('landing.startAnalyzing')}</span>
            <svg className="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
          <button className="btn btn-outline">
            <span>{t('landing.viewDocs')}</span>
          </button>
        </div>

        {/* Hero visual — Terminal/Dashboard mockup */}
        <div className="hero-visual">
          <div className="terminal-card">
            <div className="terminal-header">
              <div className="terminal-dots">
                <span className="tdot tdot-red"></span>
                <span className="tdot tdot-yellow"></span>
                <span className="tdot tdot-green"></span>
              </div>
              <span className="terminal-title">{t('landing.terminalTitle')}</span>
              <div className="terminal-dots-spacer"></div>
            </div>
            <div className="terminal-body">
              <div className="terminal-line">
                <span className="t-prompt">$</span>
                <span className="t-cmd">didi analyze</span>
                <span className="t-flag">--url</span>
                <span className="t-string">"bbc.co.uk/documentary/jan6"</span>
              </div>
              <div className="terminal-line t-muted">
                <span>{t('landing.terminalExtract')}</span>
              </div>
              <div className="terminal-line t-muted">
                <span>{t('landing.terminalPipeline')}</span>
              </div>
              <div className="terminal-spacer"></div>
              <div className="terminal-line">
                <span className="t-label">VERDICT</span>
                <span className="t-danger">{t('landing.terminalVerdict')}</span>
              </div>
              <div className="terminal-line">
                <span className="t-label">CONFIDENCE</span>
                <span className="t-value">
                  <span className="t-bar"><span className="t-bar-fill" style={{ width: '87%' }}></span></span>
                  87%
                </span>
              </div>
              <div className="terminal-line">
                <span className="t-label">SOURCES</span>
                <span className="t-success">{t('landing.terminalSources')}</span>
              </div>
              <div className="terminal-line">
                <span className="t-label">MATCH</span>
                <span className="t-warning">{t('landing.terminalMatch')}</span>
              </div>
              <div className="terminal-spacer"></div>
              <div className="terminal-line">
                <span className="t-label">KEY FINDING</span>
              </div>
              <div className="terminal-line t-finding">
                <span>{t('landing.terminalFinding1')}</span>
              </div>
              <div className="terminal-line t-finding">
                <span>{t('landing.terminalFinding2')}</span>
              </div>
              <div className="terminal-spacer"></div>
              <div className="terminal-line">
                <span className="t-prompt">$</span>
                <span className="t-cursor"></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Separator */}
      <div className="section-separator"></div>

      {/* Problem Section */}
      <section className="problem-section" id="problem" ref={problemRef}>
        <div className="section-header reveal-child">
          <span className="section-badge">{t('landing.problemBadge')}</span>
          <h2 className="section-title">{t('landing.problemTitle')}</h2>
          <p className="section-subtitle">
            {t('landing.problemSubtitle')}
          </p>
        </div>
        <div className="problem-stats-row reveal-child">
          <div className="problem-stat">
            <span className="problem-stat-number">6x</span>
            <span className="problem-stat-label">{t('landing.stat6x')}</span>
          </div>
          <div className="problem-stat-divider"></div>
          <div className="problem-stat">
            <span className="problem-stat-number">70%</span>
            <span className="problem-stat-label">{t('landing.stat70')}</span>
          </div>
          <div className="problem-stat-divider"></div>
          <div className="problem-stat">
            <span className="problem-stat-number">$78B</span>
            <span className="problem-stat-label">{t('landing.stat78B')}</span>
          </div>
        </div>
      </section>

      <div className="section-separator"></div>

      {/* Platform Section — Bento Grid */}
      <section className="platform-section" id="platform" ref={platformRef}>
        <div className="section-header reveal-child">
          <span className="section-badge">{t('landing.platformBadge')}</span>
          <h2 className="section-title">{t('landing.platformTitle')}</h2>
          <p className="section-subtitle">
            {t('landing.platformSubtitle')}
          </p>
        </div>
        <div className="platform-cards reveal-child">
          <div className="platform-card">
            <div className="platform-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="8" y1="21" x2="16" y2="21" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="17" x2="12" y2="21" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="platform-card-label">{t('landing.webApp')}</span>
            <h3>{t('landing.webAppHeading')}</h3>
            <p>{t('landing.webAppDesc')}</p>
            <span className="platform-tag">{t('landing.webAppTag')}</span>
          </div>
          <div className="platform-card">
            <div className="platform-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="18" x2="12.01" y2="18" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="platform-card-label">{t('landing.mobileApp')}</span>
            <h3>{t('landing.mobileAppHeading')}</h3>
            <p>{t('landing.mobileAppDesc')}</p>
            <span className="platform-tag">{t('landing.mobileAppTag')}</span>
          </div>
          <div className="platform-card">
            <div className="platform-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="15 3 21 3 21 9" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="10" y1="14" x2="21" y2="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="platform-card-label">{t('landing.extensionLabel')}</span>
            <h3>{t('landing.extensionHeading')}</h3>
            <p>{t('landing.extensionDesc')}</p>
            <span className="platform-tag">{t('landing.extensionTag')}</span>
          </div>
        </div>
        <div className="input-types reveal-child">
          <span className="input-types-label">{t('landing.supportedInputs')}</span>
          <div className="input-tags">
            <span>{t('common.links')}</span>
            <span>{t('common.image')}</span>
            <span>{t('common.audio')}</span>
            <span>{t('common.video')}</span>
            <span>{t('common.text')}</span>
            <span>{t('common.files')}</span>
          </div>
        </div>
      </section>

      <div className="section-separator"></div>

      {/* Use Case 1: Trump vs BBC */}
      <section className="usecase-section" id="use-cases" ref={usecase1Ref}>
        <div className="section-header reveal-child">
          <span className="section-badge">{t('landing.useCaseBadge')}</span>
          <h2 className="section-title">{t('landing.useCaseTitle')}</h2>
          <p className="section-subtitle">
            {t('landing.useCaseSubtitle')}
          </p>
        </div>
        <div className="usecase-headline reveal-child">
          <span className="usecase-case-name">Trump vs BBC — December 2024</span>
          <h3>BBC edited Trump's Jan 6 speech, removing <em>"peacefully and patriotically"</em> — making it appear he incited violence.</h3>
        </div>
        <div className="usecase-editorial reveal-child">
          <div className="usecase-card incident">
            <h4>The Manipulation</h4>
            <p>BBC's documentary spliced 3 quotes from sections 55 minutes apart into one continuous clip.</p>
            <div className="usecase-quote">
              <span className="quote-mark">&laquo;</span>
              March to the Capitol... fight like hell
              <br/><span className="usecase-omitted">"peacefully and patriotically make your voices heard"</span>
              <span className="quote-mark">&raquo;</span>
            </div>
            <p>Result: 2 executives resigned. $10B lawsuit filed.</p>
          </div>
          <div className="usecase-card detection">
            <h4>didi Detection</h4>
            <ul className="detection-list">
              <li><span className="check-icon">&#10003;</span> <span>Video transcript extracted</span></li>
              <li><span className="check-icon">&#10003;</span> <span>Source intersection performed</span></li>
              <li><span className="check-icon">&#10003;</span> <span>Original full speech found</span></li>
              <li><span className="check-icon">&#10003;</span> <span>Timeline discontinuity flagged</span></li>
              <li><span className="check-icon">&#10003;</span> <span>Missing content identified</span></li>
            </ul>
          </div>
          <div className="usecase-card result">
            <h4>didi Output</h4>
            <div className="detection-tags">
              <span className="tag-danger">Selective Editing</span>
              <span className="tag-danger">Context Omission</span>
              <span className="tag-warning">Misleading Framing</span>
            </div>
            <div className="result-output">
              <div className="result-bar"><div className="result-bar-fill" style={{ width: '68%' }}></div></div>
              <p className="result-match"><strong>68% match</strong> with original transcript</p>
              <p className="result-verdict">Key omission: call for peaceful protest removed from edited version.</p>
            </div>
          </div>
        </div>
        <div className="usecase-impact reveal-child">
          <div className="impact-stat">
            <span className="impact-value time">&lt; 30 sec</span>
            <span className="impact-label">{t('landing.detectionTime')}</span>
          </div>
          <div className="impact-stat">
            <span className="impact-value danger">Days</span>
            <span className="impact-label">{t('landing.withoutDidi')}</span>
          </div>
          <div className="impact-stat">
            <span className="impact-value success">$10B+</span>
            <span className="impact-label">{t('landing.damagePrevented')}</span>
          </div>
        </div>
      </section>

      <div className="section-separator"></div>

      {/* Use Case 2: Eli Lilly */}
      <section className="usecase-section" ref={usecase2Ref}>
        <div className="usecase-headline reveal-child">
          <span className="usecase-case-name">Eli Lilly vs Twitter Blue — November 2022</span>
          <h3>A fake "verified" account posted <em>"insulin is free now"</em> — causing stock to drop and forcing a company-wide apology.</h3>
        </div>
        <div className="usecase-editorial reveal-child">
          <div className="usecase-card incident">
            <h4>The Impersonation</h4>
            <p>After Twitter Blue launched ($8/month for verification), someone created @EliLillyandCo mimicking the pharma giant.</p>
            <div className="usecase-quote">
              <span className="quote-mark">&laquo;</span>
              We are excited to announce insulin is free now.
              <span className="quote-mark">&raquo;</span>
            </div>
            <p>Result: Stock dropped ~4.5%. Company forced to apologize.</p>
          </div>
          <div className="usecase-card detection">
            <h4>didi Detection</h4>
            <ul className="detection-list">
              <li><span className="check-icon">&#10003;</span> <span>Claim extracted from post</span></li>
              <li><span className="check-icon">&#10003;</span> <span>Official Eli Lilly website crawled</span></li>
              <li><span className="check-icon">&#10003;</span> <span>No press release found</span></li>
              <li><span className="check-icon">&#10003;</span> <span>No SEC filings match claim</span></li>
              <li><span className="check-icon">&#10003;</span> <span>Zero news coverage of announcement</span></li>
            </ul>
          </div>
          <div className="usecase-card result">
            <h4>didi Output</h4>
            <div className="detection-tags">
              <span className="tag-danger">Unverified Claim</span>
              <span className="tag-danger">No Official Source</span>
              <span className="tag-warning">High Viral Risk</span>
            </div>
            <div className="result-output">
              <div className="result-bar"><div className="result-bar-fill" style={{ width: '0%' }}></div></div>
              <p className="result-match"><strong>0% match</strong> with official company communications</p>
              <p className="result-verdict">Critical: Major corporate announcement with zero official backing.</p>
            </div>
          </div>
        </div>
        <div className="usecase-impact reveal-child">
          <div className="impact-stat">
            <span className="impact-value time">&lt; 15 sec</span>
            <span className="impact-label">{t('landing.detectionTime')}</span>
          </div>
          <div className="impact-stat">
            <span className="impact-value danger">~4.5%</span>
            <span className="impact-label">{t('landing.stockDrop')}</span>
          </div>
          <div className="impact-stat">
            <span className="impact-value success">Billions</span>
            <span className="impact-label">{t('landing.marketCap')}</span>
          </div>
        </div>
      </section>

      <div className="section-separator"></div>

      {/* Use Case 3: B2C */}
      <section className="b2c-section" id="how-it-works" ref={b2cRef}>
        <div className="section-header reveal-child">
          <span className="section-badge">{t('landing.b2cBadge')}</span>
          <h2 className="section-title">{t('landing.b2cTitle')}</h2>
          <p className="section-subtitle">
            {t('landing.b2cSubtitle')}
          </p>
        </div>
        <div className="b2c-flow reveal-child">
          <div className="b2c-step">
            <div className="b2c-num">1</div>
            <div className="b2c-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                <line x1="12" y1="18" x2="12.01" y2="18"/>
              </svg>
            </div>
            <h4>{t('landing.step1')}</h4>
            <p>{t('landing.step1Desc')}</p>
          </div>
          <div className="b2c-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
          <div className="b2c-step">
            <div className="b2c-num">2</div>
            <div className="b2c-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/>
                <polyline points="16 6 12 2 8 6"/>
                <line x1="12" y1="2" x2="12" y2="15"/>
              </svg>
            </div>
            <h4>{t('landing.step2')}</h4>
            <p>{t('landing.step2Desc')}</p>
          </div>
          <div className="b2c-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
          <div className="b2c-step">
            <div className="b2c-num">3</div>
            <div className="b2c-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
              </svg>
            </div>
            <h4>{t('landing.step3')}</h4>
            <p>{t('landing.step3Desc')}</p>
          </div>
          <div className="b2c-arrow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
          <div className="b2c-step highlight">
            <div className="b2c-num">4</div>
            <div className="b2c-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h4>{t('landing.step4')}</h4>
            <p>{t('landing.step4Desc')}</p>
          </div>
        </div>
      </section>

      <div className="section-separator"></div>

      {/* CTA Section — Waitlist */}
      <section className="cta-section" ref={ctaRef}>
        <div className="cta-content reveal-child">
          <h2 className="cta-title">{t('landing.ctaTitle')}</h2>
          <p className="cta-subtitle">
            {t('landing.ctaSubtitle')}
          </p>

          {waitlistStatus === 'success' ? (
            <div className="waitlist-success">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="waitlist-success-icon">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <p>{t('landing.ctaSuccess')}</p>
            </div>
          ) : (
            <form className="waitlist-form" onSubmit={handleWaitlistSubmit} noValidate>
              <div className="waitlist-fields">
                <input
                  type="text"
                  className="waitlist-input"
                  placeholder={t('landing.fullName')}
                  value={waitlistName}
                  onChange={(e) => setWaitlistName(e.target.value)}
                  disabled={waitlistStatus === 'submitting'}
                />
                <input
                  type="email"
                  className="waitlist-input"
                  placeholder={t('landing.emailAddress')}
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  disabled={waitlistStatus === 'submitting'}
                />
                <button
                  type="submit"
                  className="btn btn-glow btn-large waitlist-btn"
                  disabled={waitlistStatus === 'submitting'}
                >
                  <span>{waitlistStatus === 'submitting' ? t('landing.joining') : t('landing.joinWaitlist')}</span>
                  {waitlistStatus !== 'submitting' && (
                    <svg className="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  )}
                </button>
              </div>
              {waitlistError && (
                <p className="waitlist-error">{waitlistError}</p>
              )}
            </form>
          )}
        </div>
        <div className="cta-glow"></div>
      </section>

      {/* Footer — Compact */}
      <footer className="landing-footer">
        <div className="footer-compact">
          <div className="footer-left">
            <span className="logo-text" style={{ fontSize: '22px' }}>didi</span>
            <span className="footer-copy">{t('landing.copyright')}</span>
          </div>
          <div className="footer-right">
            <a href="#platform">{t('nav.platform')}</a>
            <a href="#use-cases">{t('nav.useCases')}</a>
            <a href="#">{t('landing.footerDocs')}</a>
            <a href="#">{t('landing.footerPrivacy')}</a>
            <a href="#">{t('landing.footerTerms')}</a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
